import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { Plus, Users, Pencil, Trash2, X } from 'lucide-react';

export default function Sections() {
    const { sections, addSection, updateSection, deleteSection, timeConfigs } = useData();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ name: '', rollStart: '', rollEnd: '', timeConfigId: '' });
    const [editingId, setEditingId] = useState(null);

    const openCreateModal = () => {
        setFormData({ name: '', rollStart: '', rollEnd: '', timeConfigId: '' });
        setEditingId(null);
        setIsModalOpen(true);
    };

    const openEditModal = (section) => {
        setFormData({
            name: section.name,
            rollStart: section.rollStart || '',
            rollEnd: section.rollEnd || '',
            timeConfigId: section.timeConfigId || ''
        });
        setEditingId(section.id);
        setIsModalOpen(true);
    };

    const handleDelete = async (id, name) => {
        if (window.confirm(`Are you sure you want to delete section "${name}"?`)) {
            await deleteSection(id);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.name) {
            if (editingId) {
                await updateSection(editingId, formData);
            } else {
                await addSection(formData);
            }
            setIsModalOpen(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-primary">Sections</h1>
                    <p className="text-muted-foreground">Manage class sections</p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                >
                    <Plus size={18} />
                    Add Section
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {sections.map((section) => (
                    <div key={section.id} className="bg-card border border-border/50 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all group flex flex-col items-center text-center relative">
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                            <button
                                onClick={(e) => { e.stopPropagation(); openEditModal(section); }}
                                className="p-1.5 hover:bg-muted rounded-full text-muted-foreground hover:text-foreground"
                            >
                                <Pencil size={14} />
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); handleDelete(section.id, section.name); }}
                                className="p-1.5 hover:bg-red-50 rounded-full text-red-400 hover:text-red-500"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>

                        <div className="h-16 w-16 bg-gradient-to-br from-secondary to-secondary/50 rounded-full flex items-center justify-center text-foreground mb-4 group-hover:scale-110 transition-transform shadow-inner">
                            <span className="text-2xl font-bold font-heading">{section.name.split(' ')[0].split('-').pop()}</span>
                        </div>
                        <h3 className="text-lg font-bold text-foreground">{section.name}</h3>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                            <Users size={14} />
                            <span>{section.rollStart && section.rollEnd ? `Roll: ${section.rollStart}-${section.rollEnd}` : 'Active Batch'}</span>
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card w-full max-w-md p-6 rounded-2xl shadow-2xl border border-border">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold font-heading">{editingId ? 'Edit Section' : 'Add New Section'}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground block mb-1">Section Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-secondary border-transparent focus:border-primary focus:ring-0 rounded-lg px-4 py-2.5"
                                    placeholder="e.g. Section C"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground block mb-1">Roll No Start</label>
                                    <input
                                        type="text"
                                        value={formData.rollStart}
                                        onChange={(e) => setFormData({ ...formData, rollStart: e.target.value })}
                                        className="w-full bg-secondary border-transparent focus:border-primary focus:ring-0 rounded-lg px-4 py-2.5"
                                        placeholder="e.g. 101"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground block mb-1">Roll No End</label>
                                    <input
                                        type="text"
                                        value={formData.rollEnd}
                                        onChange={(e) => setFormData({ ...formData, rollEnd: e.target.value })}
                                        className="w-full bg-secondary border-transparent focus:border-primary focus:ring-0 rounded-lg px-4 py-2.5"
                                        placeholder="e.g. 160"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground block mb-1">Timetable Schedule</label>
                                <select
                                    value={formData.timeConfigId}
                                    onChange={(e) => setFormData({ ...formData, timeConfigId: e.target.value })}
                                    className="w-full bg-secondary border-transparent focus:border-primary focus:ring-0 rounded-lg px-4 py-2.5 appearance-none"
                                >
                                    <option value="">Select Schedule...</option>
                                    {timeConfigs.map(config => (
                                        <option key={config.id} value={config.id}>
                                            {config.name} ({config.description})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-2 rounded-lg border border-border text-muted-foreground hover:bg-secondary font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
                                >
                                    {editingId ? 'Save Changes' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
